import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { GTMAgentBubble, GTMAgentSidebar } from './GTMAgent';
import CommandPalette from './CommandPalette';

// ─────────────────────────────────────────────────────────────
//  3-ZONE NAVIGATION
//  Zone 1 FIND & TARGET  → discovery, research, signals
//  Zone 2 ENGAGE & CLOSE → deals, intel, tasks, scoring
//  Zone 3 CREATE         → campaigns, drafts, approvals, publish
//  Knowledge Base        → foundation, sources, assets, analysis
//  Settings              → team, org, integrations
//
//  Hidden (still accessible via URL, not in sidebar):
//  /promptops  /learning  /agent-ops  /token-usage
//  /channels   /multichannel
// ─────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { path: '/dashboard', icon: '⊞', label: 'Dashboard',  testId: 'sidebar-nav-dashboard' },
  { path: '/agent',     icon: '🤖', label: 'GTM Agent',  testId: 'sidebar-nav-agent', highlight: true },

  // ── Zone 1: FIND & TARGET ──────────────────────────────────
  { divider: true, label: 'FIND & TARGET' },
  { path: '/apollo',      icon: '🔭', label: 'Apollo Leads',   testId: 'sidebar-nav-apollo' },
  { path: '/accounts',    icon: '🏢', label: 'Accounts',        testId: 'sidebar-nav-accounts' },
  { path: '/people',      icon: '👥', label: 'People Intel',    testId: 'sidebar-nav-people' },
  { path: '/signals',     icon: '📡', label: 'Signal Center',   testId: 'sidebar-nav-signals' },
  { path: '/competitors', icon: '⚔️', label: 'Competitors',     testId: 'sidebar-nav-competitors' },

  // ── Zone 2: ENGAGE & CLOSE ─────────────────────────────────
  { divider: true, label: 'ENGAGE & CLOSE' },
  { path: '/deals',        icon: '💰', label: 'Deal Pipeline',  testId: 'sidebar-nav-deals' },
  { path: '/intelligence', icon: '🧠', label: 'Pre-call Intel', testId: 'sidebar-nav-intelligence' },
  { path: '/crm-tasks',    icon: '📋', label: 'Follow-ups',     testId: 'sidebar-nav-crm-tasks' },
  { path: '/scoring',      icon: '📊', label: 'Lead Scoring',   testId: 'sidebar-nav-scoring' },

  // ── Zone 3: CREATE ─────────────────────────────────────────
  { divider: true, label: 'CREATE' },
  { path: '/campaigns',  icon: '🎯', label: 'Campaigns',   testId: 'sidebar-nav-campaigns' },
  { path: '/drafts',     icon: '✍️', label: 'Drafts',       testId: 'sidebar-nav-drafts' },
  { path: '/approvals',  icon: '✅', label: 'Approvals',   testId: 'sidebar-nav-approvals' },
  { path: '/publishing', icon: '📢', label: 'Publishing',  testId: 'sidebar-nav-publishing' },

  // ── Knowledge Base ─────────────────────────────────────────
  { divider: true, label: 'KNOWLEDGE BASE' },
  { path: '/foundation', icon: '🏛',  label: 'Foundation',  testId: 'sidebar-nav-foundation' },
  { path: '/sources',    icon: '🌐',  label: 'Sources',     testId: 'sidebar-nav-sources' },
  { path: '/assets',     icon: '📁',  label: 'Assets',      testId: 'sidebar-nav-assets' },
  { path: '/analysis',   icon: '🔬',  label: 'Analysis',    testId: 'sidebar-nav-analysis' },
  { path: '/ingestion',  icon: '📥',  label: 'Import Intel', testId: 'sidebar-nav-ingestion' },

  // ── Settings ───────────────────────────────────────────────
  { divider: true, label: 'SETTINGS' },
  { path: '/settings/team',         icon: '👥', label: 'Team',         testId: 'sidebar-nav-team' },
  { path: '/settings/organization', icon: '🏢', label: 'Org Settings', testId: 'sidebar-nav-org' },
  { path: '/integrations',          icon: '🔌', label: 'Integrations', testId: 'sidebar-nav-integrations' },
  { path: '/upgrade', icon: '⬆️', label: 'Upgrade Plan', testId: 'sidebar-nav-upgrade', highlight: true },
];

function CountrySelector() {
  const { activeCountry, countries, switchCountry, token } = useAuth();
  const country = activeCountry || { code: 'MX', name: 'Mexico', flag: '🇲🇽' };
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newCountry, setNewCountry] = useState({ code: '', name: '', flag: '', language: 'en', currency: 'USD', region: 'LATAM' });
  const [addError, setAddError] = useState('');
  const ref = useRef();
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSwitch = async (code) => {
    setSwitching(true);
    await switchCountry(code);
    setSwitching(false);
    setOpen(false);
  };

  const handleAddCountry = async () => {
    setAddError('');
    if (!newCountry.code || newCountry.code.length !== 2 || !newCountry.name) {
      setAddError('2-letter code and name required');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/settings/countries`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newCountry)
      });
      if (!res.ok) { const d = await res.json(); setAddError(d.detail || 'Failed'); return; }
      setShowAdd(false);
      setNewCountry({ code: '', name: '', flag: '', language: 'en', currency: 'USD', region: 'LATAM' });
      window.location.reload();
    } catch (e) { setAddError('Network error'); }
  };

  return (
    <div className="country-selector" ref={ref}>
      <button
        className="country-btn"
        onClick={() => setOpen(!open)}
        data-testid="country-selector-btn"
        title="Switch active market"
      >
        <span className="country-flag">{country.flag}</span>
        <span className="country-code">{country.code}</span>
        <span className="country-arrow">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="country-dropdown" data-testid="country-dropdown">
          <div className="country-dropdown-header">Active Market</div>
          {countries.map(c => (
            <button
              key={c.code}
              className={`country-option ${country.code === c.code ? 'active' : ''}`}
              onClick={() => handleSwitch(c.code)}
              data-testid={`country-option-${c.code}`}
              disabled={switching}
            >
              <span className="country-flag-lg">{c.flag}</span>
              <div>
                <div className="country-option-name">{c.name}</div>
                <div className="country-option-meta">{c.language?.toUpperCase()} · {c.region} · {c.market_maturity}</div>
              </div>
              {country.code === c.code && <span className="country-check">✓</span>}
            </button>
          ))}
          <div style={{borderTop:'1px solid rgba(255,255,255,0.1)', margin:'8px 0'}}></div>
          {!showAdd ? (
            <button className="country-option" onClick={() => setShowAdd(true)} style={{opacity:0.7}}>
              <span className="country-flag-lg">+</span>
              <div><div className="country-option-name">Add New Country</div></div>
            </button>
          ) : (
            <div style={{padding:'8px 12px'}}>
              <input placeholder="Code (e.g. CO)" maxLength={2} value={newCountry.code}
                onChange={e => setNewCountry({...newCountry, code: e.target.value.toUpperCase()})}
                style={{width:'100%',padding:'6px 8px',marginBottom:4,borderRadius:4,border:'1px solid rgba(255,255,255,0.2)',background:'rgba(255,255,255,0.05)',color:'#fff',fontSize:13}} />
              <input placeholder="Country name" value={newCountry.name}
                onChange={e => setNewCountry({...newCountry, name: e.target.value})}
                style={{width:'100%',padding:'6px 8px',marginBottom:4,borderRadius:4,border:'1px solid rgba(255,255,255,0.2)',background:'rgba(255,255,255,0.05)',color:'#fff',fontSize:13}} />
              <input placeholder="Flag emoji (optional)" value={newCountry.flag}
                onChange={e => setNewCountry({...newCountry, flag: e.target.value})}
                style={{width:'100%',padding:'6px 8px',marginBottom:6,borderRadius:4,border:'1px solid rgba(255,255,255,0.2)',background:'rgba(255,255,255,0.05)',color:'#fff',fontSize:13}} />
              {addError && <div style={{color:'#f87171',fontSize:12,marginBottom:4}}>{addError}</div>}
              <div style={{display:'flex',gap:6}}>
                <button onClick={handleAddCountry} style={{flex:1,padding:'6px',borderRadius:4,background:'#2563eb',color:'#fff',border:'none',cursor:'pointer',fontSize:12}}>Add</button>
                <button onClick={() => {setShowAdd(false);setAddError('');}} style={{flex:1,padding:'6px',borderRadius:4,background:'rgba(255,255,255,0.1)',color:'#fff',border:'none',cursor:'pointer',fontSize:12}}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, activeCountry, countries, switchCountry } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [splitScreen, setSplitScreen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  const country = activeCountry || { code: 'MX', name: 'Mexico', flag: '🇲🇽' };

  // Dynamic company initials from org_name
  const getInitials = (name) => {
    if (!name) return 'M';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };
  const orgName = user?.org_name || 'My Organization';
  const orgInitials = getInitials(orgName);

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo">{orgInitials}</div>
            {!collapsed && (
              <div>
                <div className="sidebar-brand-name">{orgName}</div>
                <div className="sidebar-brand-sub">Motion · AI Agent</div>
              </div>
            )}
          </div>
          <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? '→' : '←'}
          </button>
        </div>

        {/* Org plan badge */}
        {!collapsed && (
          <div className="sidebar-plan-badge">
            <div className="plan-info">
              <span className="plan-dot" style={{
                background: user?.plan === 'trial' ? '#F59E0B' :
                            user?.plan === 'enterprise' ? '#10B981' :
                            user?.plan === 'pro' ? '#0276F1' : '#9CA3AF'
              }}></span>
              <span className="plan-name">{user?.plan === 'trial' ? '⏳ Trial' : (user?.plan || 'free')}</span>
            </div>
            {user?.role === 'superadmin' && (
              <button className="superadmin-badge" onClick={() => navigate('/superadmin')}>SUPER ADMIN</button>
            )}
          </div>
        )}

        {/* Country selector in sidebar when expanded */}
        {!collapsed && (
          <div className="sidebar-country">
            <CountrySelector />
          </div>
        )}

        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item, i) => {
            if (item.divider) return (
              !collapsed && <div key={i} className="nav-divider">{item.label}</div>
            );
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                className={`nav-item ${active ? 'active' : ''} ${item.highlight && !active ? 'highlight' : ''}`}
                onClick={() => navigate(item.path)}
                data-testid={item.testId}
                title={collapsed ? item.label : ''}
              >
                <span className="nav-icon">{item.icon}</span>
                {!collapsed && (
                  <>
                    <span className="nav-label">{item.label}</span>
                    {item.badge && <span className="nav-badge">{item.badge}</span>}
                    {item.highlight && !active && <span className="nav-new-dot"></span>}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {!collapsed && (
          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar">{user?.name?.[0] || 'A'}</div>
              <div>
                <div className="user-name">{user?.name || 'Admin'}</div>
                <div className="user-role">{user?.role}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={logout} data-testid="logout-btn">Sign out</button>
              <button
                className="theme-toggle-btn"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              >
                {theme === 'light' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5"/>
                    <line x1="12" y1="1" x2="12" y2="3"/>
                    <line x1="12" y1="21" x2="12" y2="23"/>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                    <line x1="1" y1="12" x2="3" y2="12"/>
                    <line x1="21" y1="12" x2="23" y2="12"/>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                  </svg>
                )}
              </button>
              <span className="cmdk-hint" title="Command Palette">⌘K</span>
            </div>
            <div className="sidebar-version">v1.4.0 · CRMCommandCenter</div>
          </div>
        )}
      </aside>

      {/* Command Palette */}
      <CommandPalette />

      {/* Main + optional split screen */}
      <div className={`main-wrapper ${splitScreen ? 'split' : ''}`}>
        <main className="main-content">{children}</main>
        {splitScreen && (
          <div className="split-agent-panel">
            <GTMAgentSidebar onClose={() => setSplitScreen(false)} />
          </div>
        )}
      </div>

      {/* Floating GTM Agent bubble (not on /agent page) */}
      {location.pathname !== '/agent' && !splitScreen && (
        <GTMAgentBubble />
      )}

      <style>{`
        .app-layout { display: flex; min-height: 100vh; background: var(--page-bg); }
        .sidebar {
          width: 240px; min-height: 100vh;
          background: var(--headline);
          display: flex; flex-direction: column;
          flex-shrink: 0;
          transition: width 0.2s ease;
          position: sticky; top: 0; height: 100vh; overflow-y: auto;
        }
        .sidebar.collapsed { width: 60px; }
        .sidebar-header {
          padding: 16px 14px; display: flex; align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .sidebar-brand { display: flex; align-items: center; gap: 10px; overflow: hidden; }
        .sidebar-logo {
          width: 34px; height: 34px; flex-shrink: 0;
          background: var(--primary); border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 800; color: white;
        }
        .sidebar-brand-name { font-size: 13px; font-weight: 700; color: white; white-space: nowrap; }
        .sidebar-brand-sub { font-size: 10px; color: rgba(255,255,255,0.45); white-space: nowrap; }
        .collapse-btn {
          background: rgba(255,255,255,0.08); border: none; color: rgba(255,255,255,0.5);
          width: 24px; height: 24px; border-radius: 6px; cursor: pointer; font-size: 12px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .collapse-btn:hover { background: rgba(255,255,255,0.15); color: white; }
        /* Org plan badge */
        .sidebar-plan-badge { padding: 6px 12px; border-bottom: 1px solid rgba(255,255,255,.08); display: flex; align-items: center; justify-content: space-between; }
        .plan-info { display: flex; align-items: center; gap: 6px; }
        .plan-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
        .plan-name { font-size: 11px; color: rgba(255,255,255,.6); font-weight: 600; text-transform: capitalize; }
        .superadmin-badge { background: #B91C1C; color: white; border: none; border-radius: 4px; padding: 2px 7px; font-size: 9px; font-weight: 800; cursor: pointer; letter-spacing: 0.5px; }
        .superadmin-badge:hover { background: #991B1B; }

        /* Country selector in sidebar */
        .sidebar-country { padding: 8px 12px; border-bottom: 1px solid rgba(255,255,255,0.08); }
        .country-selector { position: relative; }
        .country-btn {
          width: 100%; display: flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 8px; padding: 7px 10px; cursor: pointer;
          color: white; font-size: 13px; transition: all 0.15s;
        }
        .country-btn:hover { background: rgba(255,255,255,0.14); border-color: rgba(255,255,255,0.2); }
        .country-flag { font-size: 16px; }
        .country-code { flex: 1; font-weight: 600; font-size: 12px; text-align: left; }
        .country-arrow { font-size: 8px; color: rgba(255,255,255,0.5); }
        .country-dropdown {
          position: absolute; top: calc(100% + 4px); left: 0; right: 0;
          background: white; border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.2);
          border: 1px solid var(--border-light); z-index: 200;
          overflow: hidden; min-width: 220px;
        }
        .country-dropdown-header {
          padding: 8px 12px; font-size: 10px; font-weight: 700;
          color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px;
          background: var(--page-bg); border-bottom: 1px solid var(--border-light);
        }
        .country-option {
          display: flex; align-items: center; gap: 10px;
          width: 100%; padding: 9px 12px; border: none; background: white;
          cursor: pointer; text-align: left; transition: background 0.12s;
          border-bottom: 1px solid var(--border-light);
        }
        .country-option:last-child { border-bottom: none; }
        .country-option:hover { background: var(--card-bg); }
        .country-option.active { background: rgba(2,118,241,0.06); }
        .country-option:disabled { opacity: 0.6; cursor: wait; }
        .country-flag-lg { font-size: 20px; flex-shrink: 0; }
        .country-option-name { font-size: 13px; font-weight: 600; color: var(--headline); }
        .country-option-meta { font-size: 10px; color: var(--text-muted); margin-top: 1px; text-transform: capitalize; }
        .country-check { margin-left: auto; color: var(--primary); font-weight: 700; font-size: 14px; }

        .sidebar-nav { flex: 1; padding: 12px 8px; display: flex; flex-direction: column; gap: 2px; }
        .nav-divider {
          font-size: 9px; font-weight: 700; color: rgba(255,255,255,0.3);
          letter-spacing: 1.5px; padding: 12px 8px 4px; text-transform: uppercase;
        }
        .nav-item {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 10px; border-radius: 8px;
          border: none; background: transparent; cursor: pointer;
          color: rgba(255,255,255,0.65); font-size: 13px; font-weight: 500;
          text-align: left; width: 100%; transition: all 0.15s;
        }
        .nav-item:hover { background: rgba(255,255,255,0.08); color: white; }
        .nav-item.active { background: var(--primary); color: white; font-weight: 600; }
        .nav-icon { font-size: 16px; flex-shrink: 0; width: 20px; text-align: center; }
        .nav-label { flex: 1; white-space: nowrap; }
        .nav-badge {
          font-size: 9px; background: rgba(255,255,255,0.15); color: rgba(255,255,255,0.6);
          padding: 1px 5px; border-radius: 4px; font-weight: 700;
        }
        .nav-item.active .nav-badge { background: rgba(255,255,255,0.25); color: white; }
        .sidebar-footer {
          padding: 12px; border-top: 1px solid rgba(255,255,255,0.08);
          display: flex; flex-direction: column; gap: 8px;
        }
        .sidebar-version {
          font-size: 10px; color: rgba(255,255,255,0.3); text-align: center;
          padding-top: 4px; letter-spacing: 0.3px;
        }
        .user-info { display: flex; align-items: center; gap: 8px; }
        .user-avatar {
          width: 30px; height: 30px; background: var(--primary); border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700; color: white; flex-shrink: 0;
        }
        .user-name { font-size: 12px; font-weight: 600; color: white; }
        .user-role { font-size: 10px; color: rgba(255,255,255,0.45); text-transform: capitalize; }
        .nav-item.highlight { background: rgba(2,118,241,0.12); color: rgba(255,255,255,0.9); border: 1px solid rgba(2,118,241,0.3); }
        .nav-item.highlight:hover { background: rgba(2,118,241,0.2); }
        .nav-new-dot { width: 6px; height: 6px; background: #56C1FF; border-radius: 50%; margin-left: auto; animation: pulse 2s infinite; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .theme-toggle-btn {
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 6px; padding: 4px 6px; cursor: pointer;
          color: rgba(255,255,255,0.6); display: flex; align-items: center;
          justify-content: center; transition: all 0.15s;
        }
        .theme-toggle-btn:hover { background: rgba(255,255,255,0.15); color: white; }
        .cmdk-hint {
          font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.4);
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 4px; padding: 2px 6px; cursor: default; white-space: nowrap;
        }
        .main-wrapper { flex: 1; display: flex; min-height: 100vh; overflow: hidden; }
        .main-wrapper.split { }
        .main-content { flex: 1; min-height: 100vh; overflow-y: auto; }
        .split-agent-panel { width: 420px; flex-shrink: 0; border-left: 1px solid var(--border-light); height: 100vh; overflow: hidden; }
      `}</style>
    </div>
  );
}
